import {
  Calendar,
  FileBarChart,
  FilePlus2,
  Globe,
  Home,
  LineChart,
  Package,
  Receipt,
  Settings,
  Shield,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export interface SearchPage {
  id: string;
  title: string;
  description: string;
  href: string;
  section: string;
  icon: LucideIcon;
  keywords: string[];
  adminOnly?: boolean;
  /** Hide unless the viewer can create rate cards (`create:rates`, non-sales). */
  requiresCreateRates?: boolean;
  /** Feature flag id — page hidden when the flag is off. */
  featureFlag?: string;
  /** Require this RBAC permission to surface the page. */
  requiresPermission?: string;
  /** Hide for SalesPerson accounts. */
  hideForSales?: boolean;
}

export const SEARCH_PAGES: SearchPage[] = [
  {
    id: "kai",
    title: "Kai",
    description: "Ask Kai about bookings, rates, and operations",
    href: "/",
    section: "Workspace",
    icon: Sparkles,
    keywords: ["kai", "assistant", "ai", "chat", "help", "ask"],
  },
  {
    id: "dashboard",
    title: "Home",
    description: "Your personalized operations dashboard",
    href: "/dashboard",
    section: "Workspace",
    icon: Home,
    keywords: ["dashboard", "home", "overview", "widgets", "metrics"],
  },
  {
    id: "bookings",
    title: "Bookings",
    description: "Browse and filter freight bookings",
    href: "/bookings",
    section: "Operations",
    icon: Package,
    keywords: ["bookings", "booking", "freight", "shipment", "containers"],
  },
  {
    id: "rates",
    title: "Rates",
    description: "Rate cards, contracts, and pricing",
    href: "/rates",
    section: "Operations",
    icon: LineChart,
    keywords: ["rates", "rate card", "pricing", "contract", "carrier", "tariff"],
  },
  {
    id: "rate-create",
    title: "Create rate card",
    description: "Manually build a carrier rate card or spot rate",
    href: "/rates/create",
    section: "Operations",
    icon: FilePlus2,
    keywords: [
      "create rate",
      "new rate card",
      "add rate",
      "manual rate",
      "new contract",
      "upload rate",
      "spot rate",
      "rate wizard",
    ],
    requiresCreateRates: true,
  },
  {
    id: "rate-create-document",
    title: "Create rate card from document",
    description: "Upload a rate sheet and let Kai draft the rate card",
    href: "/rates/create/from-document",
    section: "Operations",
    icon: Sparkles,
    keywords: [
      "ai rate card",
      "rate card from document",
      "upload rate sheet",
      "extract rates",
      "import rate card",
      "ocr rate",
      "pdf rate card",
    ],
    requiresCreateRates: true,
  },
  {
    id: "reports",
    title: "Reports",
    description: "Dashboards and scheduled reports",
    href: "/reports",
    section: "Analytics",
    icon: FileBarChart,
    keywords: ["reports", "dashboards", "metrics", "analytics", "scheduled"],
  },
  {
    id: "schedule",
    title: "Schedule",
    description: "Carrier schedules and sailing events",
    href: "/schedule",
    section: "Operations",
    icon: Calendar,
    keywords: ["schedule", "sailing", "vessel", "etd", "cutoff", "calendar"],
  },
  {
    id: "invoices",
    title: "Invoices",
    description: "Reconcile carrier invoices and agent charges against rates",
    href: "/invoices",
    section: "Operations",
    icon: Receipt,
    keywords: [
      "invoices",
      "invoice",
      "reconciliation",
      "reconcile",
      "debit note",
      "credit note",
      "billing",
      "finance",
    ],
    featureFlag: "invoice_reconciliation",
    requiresPermission: "read:invoices",
    hideForSales: true,
  },
  {
    id: "tracking",
    title: "Tracking",
    description: "Live vessel positions on the globe",
    href: "/tracking",
    section: "Operations",
    icon: Globe,
    keywords: ["tracking", "ais", "vessel", "globe", "map", "position"],
    featureFlag: "ais_vessel_tracking",
  },
  {
    id: "admin",
    title: "Administration",
    description: "Team, integrations, policies, and config",
    href: "/admin",
    section: "Settings",
    icon: Shield,
    keywords: ["admin", "settings", "team", "integrations", "policies"],
    adminOnly: true,
  },
  {
    id: "preferences",
    title: "Preferences",
    description: "Theme, density, and display settings",
    href: "/preferences",
    section: "Settings",
    icon: Settings,
    keywords: ["preferences", "settings", "theme", "density", "display"],
  },
];

export function filterSearchPages(
  query: string,
  pages: SearchPage[],
): SearchPage[] {
  const q = query.trim().toLowerCase();
  if (!q) return pages;
  return pages.filter((page) => {
    if (page.title.toLowerCase().includes(q)) return true;
    if (page.description.toLowerCase().includes(q)) return true;
    if (page.section.toLowerCase().includes(q)) return true;
    return page.keywords.some((kw) => kw.includes(q));
  });
}
